import { useMap } from '@vis.gl/react-google-maps';

import { Icon, IconButton, List, Popper } from '@atom/mui';
import { EditIcon } from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import fonts from '@atom/styles/fonts';

import { EditTargetView, MapEditingSlice } from '../drawing/drawingTypes';
import { formatCoordinates } from '../mapUtilities';

import classes from './toolbar.module.css';

const { ListItem, ListItemButton, ListItemText } = List;

const EDIT_LIST_Z_INDEX = 1;
const RECENTER_ZOOM = 14;
const COORDINATE_DECIMAL_PLACES = 6;

const styles = {
  list: {
    paddingTop: 0,
    paddingBottom: 0,
  },
  listItemButton: {
    paddingTop: 0,
    paddingBottom: 0,
  },
  icon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: '2.5rem',
    marginRight: '0.625rem',
  },
  name: {
    fontWeight: 700,
    fontSize: fonts.sm,
    color: colors.neutral.dark,
  },
  address: {
    display: 'block',
    color: colors.neutral.dark,
  },
  coordinates: {
    display: 'block',
  },
};

type MapEditListProps = {
  editing: MapEditingSlice;
  open: boolean;
  anchorEl: HTMLDivElement | null;
};

const MapEditList = ({ editing, open, anchorEl }: MapEditListProps) => {
  const map = useMap();

  const recenter = (coordinates: google.maps.LatLngLiteral) => {
    map?.panTo(coordinates);
    map?.setZoom(RECENTER_ZOOM);
  };

  const handleSelect = (target: EditTargetView) => () => {
    editing.onSelect(target.id);

    if (target.coordinates) {
      recenter(target.coordinates);
    }
  };

  return (
    <Popper
      open={open}
      anchorEl={anchorEl ?? undefined}
      placement="bottom-start"
      style={{ zIndex: EDIT_LIST_Z_INDEX }}
      disablePortal
    >
      <div
        className={classes.editList}
        style={{ width: anchorEl?.clientWidth }}
        data-cy="mapEditList"
      >
        <List style={styles.list}>
          {editing.targetViews.map(target => (
            <ListItem
              key={target.id}
              disablePadding
              secondaryAction={
                target.canEdit && (
                  <IconButton
                    size="small"
                    tooltip="Edit"
                    data-cy="mapEditButton"
                    onClick={() => editing.onEdit(target.id)}
                  >
                    <Icon icon={EditIcon} />
                  </IconButton>
                )
              }
            >
              <ListItemButton
                style={styles.listItemButton}
                disabled={!target.coordinates}
                onClick={handleSelect(target)}
              >
                <div style={styles.icon}>{target.icon}</div>
                <ListItemText
                  primary={target.name}
                  primaryTextStyle={styles.name}
                  secondary={
                    <>
                      {target.address && (
                        <span style={styles.address}>{target.address}</span>
                      )}
                      {target.coordinates && (
                        <span style={styles.coordinates}>
                          {formatCoordinates(
                            target.coordinates,
                            COORDINATE_DECIMAL_PLACES,
                          )}
                        </span>
                      )}
                    </>
                  }
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </div>
    </Popper>
  );
};

export default MapEditList;
